/**
 * In-app notifications (V3). Real calls against internal/notifications.
 * The read model is shared/team: opening is collective, mark-all-read is
 * per-user, delete is a per-user dismiss.
 */
import { apiClient } from '@discovr/common';
import type { DataResponse } from '@discovr/common';
import type { AppNotification } from '@discovr/common';

export type { AppNotification } from '@discovr/common';

export type NotificationTab = 'messages' | 'activities';

export interface NotificationPage {
  items: AppNotification[];
  next_cursor: string;
}

export interface UnreadCounts {
  messages: number;
  activities: number;
}

export interface NotificationPreference {
  event_type: string;
  in_app: boolean;
  email: boolean;
}

export const notifications = {
  /** Cursor-paginated inbox, newest first, scoped to one tab (and thread type on Messages). */
  list: (params?: {
    tab?: NotificationTab;
    threadType?: string;
    limit?: number;
    cursor?: string;
  }): Promise<NotificationPage> => {
    const q = new URLSearchParams();
    if (params?.tab) q.set('tab', params.tab);
    if (params?.threadType) q.set('thread_type', params.threadType);
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.cursor) q.set('cursor', params.cursor);
    const qs = q.toString();
    return apiClient
      .get<DataResponse<NotificationPage>>(`/notifications${qs ? `?${qs}` : ''}`)
      .then((r) => r.data.data);
  },

  getUnreadCounts: (): Promise<UnreadCounts> =>
    apiClient
      .get<DataResponse<UnreadCounts>>('/notifications/unread-count')
      .then((r) => r.data.data),

  /** Open a notification — collective for non-chat (clears the team badge). */
  markRead: (id: string): Promise<void> =>
    apiClient.put(`/notifications/${encodeURIComponent(id)}/read`).then(() => undefined),

  /** Clear only your badge (per-user). */
  markAllRead: (): Promise<void> => apiClient.put('/notifications/read-all').then(() => undefined),

  /** Dismiss from your inbox only. */
  delete: (id: string): Promise<void> =>
    apiClient.delete(`/notifications/${encodeURIComponent(id)}`).then(() => undefined),

  /** Per-user channel preferences (event types missing from the response default to on). */
  getPreferences: (): Promise<NotificationPreference[]> =>
    apiClient
      .get<DataResponse<NotificationPreference[]>>('/notification-preferences')
      .then((r) => r.data.data),

  /** Upsert one event type's in-app/email toggle. */
  updatePreference: (pref: NotificationPreference): Promise<void> =>
    apiClient.put('/notification-preferences', pref).then(() => undefined),
};
